import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Heading, Text, FormControl, FormLabel, Input, Button, Alert, AlertIcon,
  Stack, Icon,
} from '@chakra-ui/react';
import { FiCheck } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const DEV_ACCOUNTS = [
  { email: 'vivian@acme.test', role: 'Owner' },
  { email: 'daniel@acme.test', role: 'Admin' },
  { email: 'sam@acme.test', role: 'Developer' },
];

export default function LoginPage() {
  const { authenticated, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = (location.state && location.state.from) || '/';

  const params = new URLSearchParams(location.search);
  const [email, setEmail] = useState(params.get('as') || 'vivian@acme.test');
  const [password, setPassword] = useState('password123');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && authenticated) navigate(dest, { replace: true });
  }, [loading, authenticated, navigate, dest]);

  // Dev-only convenience: /login?auto=1[&as=email] signs in with the seeded
  // password so headless checks don't need form automation.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (loading || authenticated || busy) return;
    if (params.get('auto') !== '1') return;
    setBusy(true);
    login(params.get('as') || 'vivian@acme.test', 'password123')
      .then(() => navigate(dest, { replace: true }))
      .catch((e) => setError(e.message || 'auto sign-in failed'))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, authenticated]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.message || 'Sign in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.50" p={4}>
      <Box w="full" maxW="380px">
        <Flex align="center" gap={2.5} mb={7}>
          <Flex boxSize="26px" borderRadius="8px" bg="brand.500" align="center" justify="center">
            <Icon as={FiCheck} color="white" boxSize={4} />
          </Flex>
          <Text fontWeight="700" fontSize="15px" letterSpacing="-0.01em">QA Dashboard</Text>
        </Flex>

        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={7} boxShadow="card">
          <Heading size="md" fontWeight="700" letterSpacing="-0.02em" mb={1}>Sign in</Heading>
          <Text fontSize="13px" color="gray.500" mb={5}>Use your organisation account.</Text>

          {error && (
            <Alert status="error" borderRadius="md" mb={4} fontSize="13px" py={2}>
              <AlertIcon />
              {error}
            </Alert>
          )}

          <form onSubmit={submit}>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel fontSize="12.5px" color="gray.600" mb={1.5}>Email</FormLabel>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="username" size="md" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="12.5px" color="gray.600" mb={1.5}>Password</FormLabel>
                <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="current-password" size="md" />
              </FormControl>
              <Button type="submit" isLoading={busy} size="md" mt={1}>Sign in</Button>
            </Stack>
          </form>
        </Box>

        {process.env.NODE_ENV !== 'production' && (
          <Box mt={4} fontSize="12px" color="gray.500">
            <Text fontWeight="600" mb={1}>Dev accounts · password <Box as="code" fontFamily="mono">password123</Box></Text>
            {DEV_ACCOUNTS.map((a) => (
              <Text
                key={a.email}
                as="button"
                display="block"
                textAlign="left"
                _hover={{ color: 'brand.600' }}
                onClick={() => setEmail(a.email)}
              >
                {a.email} — {a.role}
              </Text>
            ))}
          </Box>
        )}
      </Box>
    </Flex>
  );
}
